import React, {useCallback, useEffect, useRef, useState} from 'react';
import {SupplierBookingTabs, transformBooking} from '../../components/bookingReports/supplier/data';
import {formatTabHeader} from '../../components/bookingReports/data';
import {Button, Grid, Tab, Tabs} from "@mui/material";
import AdjustBookingDialog from '../../components/bookingReports/AdjustBookingDialog';
import {TabContext} from "@mui/lab";
import BookingTab from '../../components/bookingReports/BookingTab';
import {SupplierBookingFilter} from '../../components/bookingReports';
import {getErrorMessage} from '../../util/NetworkErrorUtil';
import {
  getSupplierBookingCSVDownload,
  getSupplierBookings,
  postBookingStatusUpdate
} from '../../components/bookedSpaces/request/booked-spaces-requests';
import DownloadCSVButton from '../../components/report/export/DownloadCSVButton';
import {hasAdminAccess} from '../../util/AccountUtils';
import {BookingTabPanel} from '../view-styles/bookingStyles';
import useUrlQueryParams from "../../hooks/useUrlQueryParams";
import {parseCurrencyValue} from "../../util/PaymentUtils";
import {AccountType} from '../../components/constants/securspace-constants';
import {useFeatureFlags} from "../../hooks/routing/useFeatureFlags";
import {TWO_MINUTES_MILLISECONDS} from '../../components/constants/time-constants';
import useInterval from '../../hooks/useInterval';
import {SecurSpaceContainer, SecurSpaceReportContainer, SecurSpaceReportTitle} from "../../components/common";
import {SortFilterContainer} from "../../components/common/reports/sortFilter";
import {useSnackbar} from "../../context/snackbar-context";

export const adjustBookingTypes = {DATE: "End Date", SPACES: "Number of Spaces"};

export const updatePendingBookingActions = {APPROVE: "approve", DECLINE: "decline"};

const SupplierBooking = (props) => {
  const {account} = props;
  const {createManualBrokeredBooking} = useFeatureFlags();
  const { snackbarShowMessage } = useSnackbar();

  const urlParams = useUrlQueryParams();

  const getDefaultTab = useCallback((status) => {
    return SupplierBookingTabs[status?.toUpperCase()] || SupplierBookingTabs.ACTIVE;
  }, []);

  const defaultAdjustBooking = {open: false, booking: null, type: adjustBookingTypes.DATE};

  const [initialTab, setInitialTab] = useState(getDefaultTab(urlParams.status || ""));
  const [initialBookingNumber, setInitialBookingNumber] = useState(urlParams.bookingNumber || "");
  const [currentTab, setCurrentTab] = useState(initialTab);
  const [activeBookings, setActiveBookings] = useState([])
  const [inactiveBookings, setInactiveBookings] = useState([]);
  const [failedBookings, setFailedBookings] = useState([]);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [activePage, setActivePage] = useState(0);
  const [failedPage, setFailedPage] = useState(0);
  const [inactivePage, setInactivePage] = useState(0);
  const [pendingPage, setPendingPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [activeBookingCount, setActiveBookingCount] = useState(0);
  const [failedBookingCount, setFailedBookingCount] = useState(0);
  const [inactiveBookingCount, setInactiveBookingCount] = useState(0);
  const [pendingBookingCount, setPendingBookingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(null);
  const [adjustBooking, setAdjustBooking] = useState(defaultAdjustBooking);

  const userIsAdmin = hasAdminAccess(account);

  const scrollPositions = useRef({});

  const handleTabChange = (_event, value) => {
    // Save current scroll position
    scrollPositions.current[currentTab] = window.scrollY;

    setCurrentTab(value || SupplierBookingTabs.ACTIVE);

    // Restore scroll position for new tab
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPositions.current[value] || 0);
    });
  };

  const onCloseDialog = () => {
    setAdjustBooking(defaultAdjustBooking)
  }

  const reloadAllBookings = useCallback(() => {
    const bookingTabs = [
      {
        status: SupplierBookingTabs.ACTIVE,
        setBookings: setActiveBookings,
        setCount: setActiveBookingCount,
        page: activePage
      },
      {
        status: SupplierBookingTabs.INACTIVE,
        setBookings: setInactiveBookings,
        setCount: setInactiveBookingCount,
        page: inactivePage
      },
      {
        status: SupplierBookingTabs.FAILED,
        setBookings: setFailedBookings,
        setCount: setFailedBookingCount,
        page: failedPage
      },
      {
        status: SupplierBookingTabs.PENDING,
        setBookings: setPendingBookings,
        setCount: setPendingBookingCount,
        page: pendingPage
      },
    ];

    bookingTabs.forEach(({status, setBookings, setCount, page}) => {
      loadBookings(status, setBookings, setCount, page, rowsPerPage);
    });
  }, [activePage, inactivePage, failedPage, pendingPage, rowsPerPage]);

  const onDialogSuccess = () => {
    reloadAllBookings();
  }

  const handleSelectAdjustBooking = (booking, adjustType) => {
    setAdjustBooking({open: true, booking: booking, type: adjustType})
  }

  const handleAdjustSpaces = (spaces) => {
    const {booking} = adjustBooking;

    let newBooking = {...booking, numberOfSpaces: spaces}

    setAdjustBooking(prev => ({...prev, booking: newBooking}))
  }

  const handleAdjustEndDate = (date) => {
    const {booking} = adjustBooking;

    let newBooking = {...booking, endDate: date}

    setAdjustBooking(prev => ({...prev, booking: newBooking}));
  }

  /**
   * Handle adjusting booking fields
   * @param event where the target name is the booking field to adjust
   */
  const handleAdjustBooking = (event) => {
    const {booking} = adjustBooking;
    const {name, value} = event.target;
    let newBooking = {...booking};

    if (name === 'brokeredBuyerChargedPerOccurrence' || name === 'brokeredSupplierPaidPerOccurrence') {
      newBooking[name] = parseCurrencyValue(value);
    } else {
      newBooking[name] = value;
    }
    setAdjustBooking(prev => ({...prev, booking: newBooking}));
  }

  const handleUpdatePendingBooking = (booking, updateAction) => {
    let message = `Successfully ${updateAction === updatePendingBookingActions.APPROVE ? 'Approved' : 'Declined'} Booking ${booking.orderNumber}`;

    const params = {
      status: updateAction,
      body: {
        bookingId: booking.id
      }
    }

    postBookingStatusUpdate(params)
      .then(() => {
        reloadAllBookings();
        snackbarShowMessage(message, 'success');
      })
      .catch((err) => {
        snackbarShowMessage(getErrorMessage(err), "error", 15000)
      })
  }

  const loadBookings = useCallback((
    status,
    setBookings,
    setBookingCount,
    page,
    pageSize
  ) => {
    const {id} = account;

    if (id && query) {
      setLoading(true);
      const requestParams = {
        accountId: account.id,
        buyerName: query.buyerName,
        locationId: query.selectedLocation?.id || '',
        activeStatus: status,
        page: page,
        size: pageSize,
        ...query,
      };

      getSupplierBookings(requestParams)
        .then(({body}) => {
          const {count} = body;
          setBookingCount(count);
          const data = body.content?.map((booking) => {
            return transformBooking(booking);
          });
          setBookings(data);
        })
        .catch(err => {
          snackbarShowMessage(getErrorMessage(err), "error", 15000)
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [account, query, snackbarShowMessage]);

  useEffect(() => {
    loadBookings(SupplierBookingTabs.ACTIVE, setActiveBookings, setActiveBookingCount, activePage, rowsPerPage);
  }, [loadBookings, rowsPerPage, activePage]);

  useEffect(() => {
    loadBookings(SupplierBookingTabs.INACTIVE, setInactiveBookings, setInactiveBookingCount, inactivePage, rowsPerPage);
  }, [loadBookings, rowsPerPage, inactivePage]);

  useEffect(() => {
    loadBookings(SupplierBookingTabs.FAILED, setFailedBookings, setFailedBookingCount, failedPage, rowsPerPage);
  }, [loadBookings, rowsPerPage, failedPage]);

  useEffect(() => {
    loadBookings(SupplierBookingTabs.PENDING, setPendingBookings, setPendingBookingCount, pendingPage, rowsPerPage);
  }, [loadBookings, rowsPerPage, pendingPage]);

  useInterval(() => {
    loadBookings(SupplierBookingTabs.PENDING, setPendingBookings, setPendingBookingCount, pendingPage, rowsPerPage);
  }, TWO_MINUTES_MILLISECONDS);

  useEffect(() => {
    if (failedBookings && inactiveBookings && pendingBookings && activeBookings) {
      if (initialTab) {
        setInitialTab(null);
      } else if (initialBookingNumber) {
        if (activeBookings.length) {
          setInitialBookingNumber(null);
          setCurrentTab(SupplierBookingTabs.ACTIVE)
        } else if (failedBookings.length) {
          setInitialBookingNumber(null);
          setCurrentTab(SupplierBookingTabs.FAILED)
        } else if (inactiveBookings.length) {
          setInitialBookingNumber(null);
          setCurrentTab(SupplierBookingTabs.INACTIVE)
        } else if (pendingBookings.length) {
          setInitialBookingNumber(null);
          setCurrentTab(SupplierBookingTabs.PENDING)
        } else  { // default to active, but do not clear initial booking number in case bookings load after this check
          setCurrentTab(SupplierBookingTabs.ACTIVE)
        }
      }
    }
  }, [initialTab, initialBookingNumber, failedBookings, inactiveBookings, pendingBookings, activeBookings]);

  return (
    <SecurSpaceContainer>
      <SecurSpaceReportContainer>
        <SecurSpaceReportTitle>Bookings</SecurSpaceReportTitle>
        <SortFilterContainer mt={3}>
          <SupplierBookingFilter setQuery={setQuery}/>
        </SortFilterContainer>
        <Grid container item justifyContent={'flex-end'} spacing={2}>
          {
            (activeBookingCount !== 0 || inactiveBookingCount !== 0 || failedBookingCount !== 0 || pendingBookingCount !== 0) &&
            <Grid item xs={12} sm={'auto'}>
              <DownloadCSVButton
                className={'w-100'}
                title='Download CSV (UTC)'
                reportUrl={query ? getSupplierBookingCSVDownload(query) : ''}
              />
            </Grid>
          }
          {
            hasAdminAccess(account) && createManualBrokeredBooking &&
            <Grid item xs={12} sm={'auto'}>
              <Button
                href={`/booking/broker`}
                variant={'contained'}
                fullWidth
              >
                Create Brokered Booking
              </Button>
            </Grid>
          }
        </Grid>
        <Grid container flexDirection={'column'}>
          <TabContext value={currentTab}>
            <Grid container justifyContent={'space-between'} alignItems={'center'}>
              <Grid item xs>
                <Tabs
                  value={currentTab}
                  onChange={handleTabChange}
                  aria-label={'Bookings Tabs'}
                  textColor={'secondary'}
                  variant={'fullWidth'}
                >
                  <Tab value={SupplierBookingTabs.ACTIVE}
                       label={formatTabHeader(SupplierBookingTabs.ACTIVE, activeBookingCount)}/>
                  <Tab value={SupplierBookingTabs.PENDING}
                       label={formatTabHeader(SupplierBookingTabs.PENDING, pendingBookingCount)}/>
                  <Tab value={SupplierBookingTabs.INACTIVE}
                       label={formatTabHeader(SupplierBookingTabs.INACTIVE, inactiveBookingCount)}/>
                  <Tab value={SupplierBookingTabs.FAILED}
                       label={formatTabHeader(SupplierBookingTabs.FAILED, failedBookingCount)}/>
                </Tabs>
              </Grid>
            </Grid>

            <BookingTabPanel value={SupplierBookingTabs.ACTIVE}>
              <BookingTab
                bookings={activeBookings}
                loading={loading}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                page={activePage}
                setPage={setActivePage}
                count={activeBookingCount}
                status={SupplierBookingTabs.ACTIVE}
                userIsAdmin={userIsAdmin}
                handleSelectAdjustBooking={handleSelectAdjustBooking}
                accountType={AccountType.SUPPLIER}
              />
            </BookingTabPanel>
            <BookingTabPanel value={SupplierBookingTabs.PENDING}>
              <BookingTab
                bookings={pendingBookings}
                loading={loading}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                page={pendingPage}
                setPage={setPendingPage}
                count={pendingBookingCount}
                status={SupplierBookingTabs.PENDING}
                userIsAdmin={userIsAdmin}
                handleSelectAdjustBooking={handleSelectAdjustBooking}
                handleUpdatePendingBooking={handleUpdatePendingBooking}
                accountType={AccountType.SUPPLIER}
              />
            </BookingTabPanel>
            <BookingTabPanel value={SupplierBookingTabs.INACTIVE}>
              <BookingTab
                bookings={inactiveBookings}
                loading={loading}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                page={inactivePage}
                setPage={setInactivePage}
                count={inactiveBookingCount}
                status={SupplierBookingTabs.INACTIVE}
                userIsAdmin={userIsAdmin}
                handleSelectAdjustBooking={handleSelectAdjustBooking}
                accountType={AccountType.SUPPLIER}
              />
            </BookingTabPanel>
            <BookingTabPanel value={SupplierBookingTabs.FAILED}>
              <BookingTab
                bookings={failedBookings}
                loading={loading}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                page={failedPage}
                setPage={setFailedPage}
                count={failedBookingCount}
                status={SupplierBookingTabs.FAILED}
                userIsAdmin={userIsAdmin}
                handleSelectAdjustBooking={handleSelectAdjustBooking}
                accountType={AccountType.SUPPLIER}
              />
            </BookingTabPanel>
          </TabContext>
        </Grid>
        <AdjustBookingDialog
          open={adjustBooking.open}
          onConfirm={onDialogSuccess}
          onClose={onCloseDialog}
          type={adjustBooking.type}
          booking={adjustBooking.booking}
          handleAdjustEndDate={handleAdjustEndDate}
          handleAdjustSpaces={handleAdjustSpaces}
          handleAdjustBooking={handleAdjustBooking}
        />
      </SecurSpaceReportContainer>
    </SecurSpaceContainer>
  );
};

export default SupplierBooking;
